// Context types for URL query param (from_app values)
export type NavigationContext = 'tpa' | 'display' | 'reports'

// Tab names that can appear in navigation
export type NavigationTab =
  | 'Campaigns'
  | 'Reports'
  | 'Billing'
  | 'Insights'
  | 'Planning'

// The current application - Reports is always the active tab
export const CURRENT_APPLICATION: NavigationTab = 'Reports'

// Default context when no from_app query param is provided
// This is intentionally 'reports' (most restrictive) for direct URL access/bookmarks
// External apps (TPA, Display) should always include from_app param when linking:
export const DEFAULT_CONTEXT: NavigationContext = 'reports'

// Storage key for persisting navigation context
const NAVIGATION_CONTEXT_KEY = 'navigation_context'

// Valid context values for validation
const VALID_CONTEXTS: NavigationContext[] = ['tpa', 'display', 'reports']

/**
 * Get the stored navigation context from sessionStorage
 */
export const getStoredContext = (): NavigationContext | null => {
  try {
    const stored = sessionStorage.getItem(NAVIGATION_CONTEXT_KEY)
    if (stored && VALID_CONTEXTS.includes(stored as NavigationContext)) {
      return stored as NavigationContext
    }
  } catch {
    // sessionStorage may not be available
  }
  return null
}

/**
 * Store the navigation context in sessionStorage
 */
export const setStoredContext = (context: NavigationContext): void => {
  try {
    sessionStorage.setItem(NAVIGATION_CONTEXT_KEY, context)
  } catch {
    // sessionStorage may not be available
  }
}

/**
 * Clear the stored navigation context
 */
export const clearStoredContext = (): void => {
  try {
    sessionStorage.removeItem(NAVIGATION_CONTEXT_KEY)
  } catch {
    // sessionStorage may not be available
  }
}

export const ContextualNavigation: Record<
  'internal' | 'external',
  Record<NavigationContext, NavigationTab[]>
> = {
  internal: {
    tpa: ['Campaigns', 'Reports', 'Insights', 'Billing'],
    display: ['Planning', 'Reports', 'Insights'],
    reports: ['Reports', 'Insights'],
  },
  external: {
    tpa: ['Campaigns', 'Reports', 'Billing'],
    display: ['Planning', 'Reports'],
    reports: ['Reports'],
  },
}

export const getVisibleTabs = (
  context: NavigationContext | null | undefined,
  isInternalUser: boolean,
): NavigationTab[] => {
  // Default to 'reports' context if not provided
  const effectiveContext: NavigationContext =
    context || getStoredContext() || DEFAULT_CONTEXT

  // Determine user type
  const userType = isInternalUser ? 'internal' : 'external'

  // Get tabs available for this context and user type
  const visibleTabs = ContextualNavigation[userType][effectiveContext] || []

  // Ensure Reports is always included (it's the current application)
  if (!visibleTabs.includes(CURRENT_APPLICATION)) {
    return [CURRENT_APPLICATION, ...visibleTabs]
  }

  return visibleTabs
}

//parse the from_app query param from URL, with fallback to stored context
export const parseContextParam = (
  searchParams: URLSearchParams,
): NavigationContext | null => {
  const contextParam = searchParams.get('from_app')

  // If from_app is explicitly provided in URL, store it and return
  if (
    contextParam &&
    VALID_CONTEXTS.includes(contextParam as NavigationContext)
  ) {
    const context = contextParam as NavigationContext
    setStoredContext(context)
    return context
  }

  // Otherwise, return the stored context (retains previous context during navigation)
  return getStoredContext()
}
